export interface ChildName {
    /** The candidate name: an explicit name, or a slugified label. */
    name: string
    /** Whether `name` was explicitly passed in (vs. derived from the label). */
    isExplicit: boolean
}

/**
 * De-duplicate child names, returning the final name for each entry in its
 * original order.
 *
 * Explicit names are preferred and kept as-is wherever possible: they are
 * reserved first, and the only time an explicit name is changed is when two
 * explicit names are identical (the later one is suffixed). Generated names are
 * assigned afterwards and yield to anything already taken — if a generated name
 * would collide with an existing name (explicit or previously assigned), the
 * generated one is suffixed (`base`, `base_1`, `base_2`, …).
 */
export function deduplicateNames(entries: ChildName[]): string[] {
    const used = new Set<string>()
    const result: string[] = new Array(entries.length)

    const claim = (base: string): string => {
        let candidate = base
        let suffix = 1
        while (used.has(candidate)) {
            candidate = `${base}_${suffix}`
            suffix++
        }
        used.add(candidate)
        return candidate
    }

    // Pass 1: reserve explicit names. Identical explicit names are de-duped
    // against each other (later one suffixed) — the only time we touch them.
    entries.forEach((entry, i) => {
        if (entry.isExplicit) {
            result[i] = claim(entry.name)
        }
    })

    // Pass 2: assign generated names, suffixing any that collide with a name
    // already taken by an explicit or previously-assigned generated name.
    entries.forEach((entry, i) => {
        if (!entry.isExplicit) {
            result[i] = claim(entry.name)
        }
    })

    return result
}
